/* eslint-disable no-param-reassign */
// Transport-independent lifecycle. No network-state heuristic or batching timer.
// Acknowledgements close batches; publication waits for every referenced record.
export function createQueueEngine({
  store, transport, ownerId, makeId, onError, onPublished = () => {},
}) {
  const running = new Map();
  let disposed = false;
  const active = (state) => !disposed && state.active && running.get(state.batch.id) === state;

  const report = (error, state, type, recordId) => {
    if (active(state)) {
      const { batch } = state;
      onError(error, {
        inventoryId: batch.inventoryId, updateId: batch.id, type: type || 'update', recordId, code: error.code,
      });
    }
  };
  const detach = (id) => {
    const state = running.get(id);
    if (state) {
      state.active = false; clearTimeout(state.retryTimer); state.stop?.(); running.delete(id);
    }
  };
  const finish = async (state, batch) => {
    if (!active(state)) return;
    await store.remove(batch.id);
    if (!active(state)) return;
    detach(batch.id);
    if (!disposed) onPublished(batch);
  };
  const advance = async (state) => {
    if (!active(state) || state.blocked || state.retryTimer) return;
    if (state.busy) { state.dirty = true; return; }
    state.busy = true;
    try {
      let batch = await store.get(state.batch.id);
      if (!batch || !active(state)) return;
      if (state.confirmed.size) batch = await store.close(batch.id);
      if (!active(state) || !batch || !batch.closed || !batch.records.every((r) => state.confirmed.has(r.id))) return;
      // The adapter creates once in a transaction; existing processed/failed
      // batches are never reset, including recovery after a lost acknowledgement.
      try {
        await transport.publish(batch);
      } catch (error) {
        if (!active(state)) return;
        const code = error.code?.replace('firestore/', '');
        if (!['unavailable', 'deadline-exceeded', 'aborted'].includes(code)) throw error;
        // Transactions are not queued offline by the SDK. Keep the durable
        // batch and retry only this transient publication failure, with backoff.
        state.retryDelay = Math.min((state.retryDelay || 500) * 2, 30000);
        state.retryTimer = setTimeout(() => {
          state.retryTimer = null;
          advance(state);
        }, state.retryDelay);
        return;
      }
      if (active(state)) await finish(state, batch);
    } catch (error) {
      state.blocked = true;
      report(error, state);
    } finally {
      state.busy = false;
      if (state.dirty) { state.dirty = false; advance(state); }
    }
  };
  const acknowledge = (state, ids) => {
    ids.forEach((id) => state.confirmed.add(id));
    advance(state);
  };
  const write = (state, record) => {
    if (disposed || !state.active
      || state.writing.has(record.id) || state.attempted.has(record.id) || state.confirmed.has(record.id)) return;
    state.attempted.add(record.id);
    state.writing.add(record.id);
    Promise.resolve().then(async () => {
      const saved = record.data ? record : await store.record(record.id);
      // Legacy journals contain ids only; never invent missing source payloads.
      if (disposed || !state.active || state.confirmed.has(record.id)) return undefined;
      if (!saved?.data) {
        // Recheck after the local read: a newer snapshot or another tab may
        // have recovered the record/batch while that read was pending.
        const current = await store.get(state.batch.id);
        if (!disposed && state.active && state.server && current
          && !state.confirmed.has(record.id) && !state.present.has(record.id)) {
          report(new Error(
            `Record unavailable without local payload: inventoryId=${current.inventoryId}, `
            + `updateId=${current.id}, recordId=${record.id}`,
          ), state, 'missing-record', record.id);
        }
        // Keep watching: later confirmation can still release the whole batch.
        return undefined;
      }
      return transport.writeRecord(state.batch, saved).then(() => {
        if (!disposed && state.active) acknowledge(state, [record.id]);
      });
    }).catch((error) => report(error, state, 'record', record.id))
      .finally(() => state.writing.delete(record.id));
  };
  const attach = (batch) => {
    if (disposed) return null;
    if (running.has(batch.id)) return running.get(batch.id);
    const state = {
      batch,
      confirmed: new Set(),
      present: new Set(),
      attempted: new Set(),
      writing: new Set(),
      server: false,
      active: true,
    };
    running.set(batch.id, state);
    state.stop = transport.watch(batch, {
      records: async ({ fromCache, confirmedIds, presentIds }) => {
        if (disposed || !state.active) return;
        state.present = new Set(presentIds);
        if (fromCache) { state.server = false; return; }
        if (!state.server) {
          state.blocked = false; state.attempted.clear();
          clearTimeout(state.retryTimer); state.retryTimer = null;
        }
        state.server = true;
        try {
          const current = await store.get(batch.id);
          if (!current || disposed || !state.active) return;
          // Use current membership: another acknowledgement may just have closed
          // the batch, or a same-tab enqueue may have appended another record.
          acknowledge(state, confirmedIds.filter((id) => current.records.some((r) => r.id === id)));
          current.records.filter((r) => !presentIds.includes(r.id)).forEach((record) => write(state, record));
        } catch (error) { report(error, state); }
      },
      published: async (recordIds) => {
        try {
          const current = await store.get(batch.id);
          if (!current || disposed || !state.active) return;
          transport.validateMembership(current, recordIds);
          await finish(state, current);
        } catch (error) { state.blocked = true; report(error, state); }
      },
      error: (error) => { state.blocked = true; report(error, state); },
    });
    return state;
  };
  const refresh = async () => {
    const batches = await store.list();
    if (disposed) return;
    batches.forEach(attach);
    [...running.keys()].filter((id) => !batches.some((b) => b.id === id)).forEach(detach);
  };
  return {
    refresh,
    enqueue: async (inventoryId, payload) => {
      const saved = await store.append({
        inventoryId, ownerId, batchId: `upd_${makeId()}`, recordId: makeId(), payload,
      });
      if (!disposed) write(attach(saved.batch), saved.record);
      return saved;
    },
    retry: async () => {
      [...running.keys()].forEach(detach);
      await refresh();
    },
    dispose: () => { disposed = true; [...running.keys()].forEach(detach); },
  };
}
